"use client";

import { RingChart } from "@/components/charts/ring-chart";
import { Ring } from "@/components/charts/ring";
import { RingCenter } from "@/components/charts/ring-center";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ChartDataPoint } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { JSX, useMemo, useState } from "react";
import { CHART_PLOT_HEIGHT, CHART_PLOT_WIDTH } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const CHART_HEIGHT = CHART_PLOT_HEIGHT.radialBar;
const CHART_WIDTH = CHART_PLOT_WIDTH.radialBar;

export default function ModuleRadialBarChart({
  data = [],
  grayscale,
  accent,
  className,
}: {
  data?: ChartDataPoint[];
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    setActiveIndex,
    legendSelectedIndex,
  } = useLegendClickSync();
  const palette = accentChartColors(grayscale, accent);

  const valueKey = useMemo(() => {
    if (!data || data.length === 0) return "value";
    const first = data[0];
    if (!first) return "value";
    const keys = Object.keys(first).filter((k) => k !== "name" && k !== "color");
    return keys[0] ?? "value";
  }, [data]);

  const values = useMemo(
    () =>
      data.map((item) => {
        const v = item[valueKey];
        return typeof v === "number" ? v : Number(v) || 0;
      }),
    [data, valueKey],
  );

  const maxValue = useMemo(
    () => (values.length > 0 ? Math.max(...values, 1) : 1),
    [values],
  );

  const ringData = useMemo(
    () =>
      data.map((item, i) => ({
        label: stripInlineMarkdown(String(item.name)),
        value: values[i] ?? 0,
        maxValue,
        color:
          typeof item.color === "string" && item.color
            ? item.color
            : palette[i % palette.length],
      })),
    [data, values, maxValue, palette],
  );

  const total = useMemo(
    () => values.reduce((sum, v) => sum + v, 0),
    [values],
  );

  const legendItems = useMemo(
    () =>
      ringData.map((item) => ({
        label: item.label,
        value: item.value,
        maxValue: total || 1,
        color: item.color ?? "#000",
      })),
    [ringData, total],
  );

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <div className="flex w-full items-center justify-center">
        <div style={{ width: CHART_WIDTH, height: CHART_HEIGHT }}>
          <RingChart
            data={ringData}
            strokeWidth={12}
            ringGap={6}
            baseInnerRadius={28}
            hoveredIndex={hoveredIndex}
            onHoverChange={setHoveredIndex}
            selectedIndex={activeIndex}
            onSelectChange={setActiveIndex}
          >
            {ringData.map((_, i) => (
              <Ring key={i} index={i} lineCap="round" />
            ))}
            <RingCenter />
          </RingChart>
        </div>
      </div>

      <SwipeScroll className="w-full pb-1" fade="auto">
        <Legend
          items={legendItems}
          hoveredIndex={hoveredIndex}
          onHoverChange={setHoveredIndex}
          selectedIndex={legendSelectedIndex}
          onSelect={handleSelect}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-6"
        >
          <LegendItem className="flex shrink-0 items-center gap-2">
            <LegendMarker className="h-2.5 w-2.5" />
            <LegendLabel />
            <LegendValue
              showPercentage
              className="text-[11px] text-neutral-500 dark:text-neutral-400"
            />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
  );
}
